import type { DomainStore } from '~/domain/changes/DomainStore';
import type { Image3D } from '~/domain/patient/image3d';
import type { Patient } from '~/domain/patient/patient';
import type {
    DatasetEnvironmentRegistry,
    DatasetRegistryEntry,
} from '~/engine/scene/DatasetEnvironmentRegistry';
import { localization } from '~/i18n';
import { seriesSummaryOf } from '~/utils/transforms/imageDataTransforms';
import { toFrameOfReferenceUid, SeriesInstanceUid } from '~/domain/dicom/uids';
import {
    DatasetAdapter,
    DatasetMetadata,
    RenderableVolume,
} from '~/viewportGrid/adapters/types';

/** The registry surface the adapter reads. */
type DatasetRegistry = Pick<
    DatasetEnvironmentRegistry,
    'listEntries' | 'entryFor' | 'subscribeLifecycle'
>;

/** One image's volume, kept as one object, and the metadata it shows. */
interface CachedVolume {
    readonly volume: RenderableVolume;
    metadata: DatasetMetadata;
}

/** The list last handed out, with the entries it was read from. */
interface ListedDatasets {
    readonly entries: ReadonlyArray<DatasetRegistryEntry>;
    readonly metadata: ReadonlyArray<DatasetMetadata>;
}

/**
 * Engine-backed `DatasetAdapter`: answers "which datasets are bindable" from the
 * {@link DatasetEnvironmentRegistry}'s imported `Image3D`s. Metadata only — no
 * pixel access, no GPU. This is the ACL: it is allowed to read engine/domain
 * types and maps them to the grid's engine-free DTOs (branded ids, mm spacing).
 * The list and each volume stay the same objects until a register or an
 * unregister changes the dataset they describe. A committed change to an
 * image, such as a rename, refreshes the metadata its volume shows and the
 * list, and keeps the volume object, so no tile remounts for it.
 *
 * It serves one patient: only the images `patient` holds are listed or
 * resolved, so another patient's image never reaches the grid. With no
 * patient, it serves nothing.
 */
export class RegistryDatasetAdapter implements DatasetAdapter {
    // A stable RenderableVolume per image. `resolve` is called on every grid
    // re-render; returning a fresh object each time would churn the tile's mount
    // effect (remount → re-upload → texture thrash). Keyed by the image, so a
    // re-imported series gets metadata read from its new image.
    private readonly volumeCache = new WeakMap<Image3D, CachedVolume>();
    private listed: ListedDatasets | undefined;

    /**
     * @param domainChanges The session's committed domain changes. A change
     *     to an image refreshes what the adapter says about it.
     */
    public constructor(
        private readonly registry: DatasetRegistry,
        private readonly patient: Pick<Patient, 'images'> | undefined,
        private readonly domainChanges?: Pick<DomainStore, 'onChangeset'>
    ) {}

    public listAvailable(): ReadonlyArray<DatasetMetadata> {
        const entries = this.registry
            .listEntries()
            .filter((entry) => this.holds(entry));
        if (this.listed && sameDatasets(this.listed.entries, entries)) {
            return this.listed.metadata;
        }
        const metadata = entries.map((entry) => this.volumeOf(entry).metadata);
        this.listed = { entries, metadata };
        return metadata;
    }

    public resolve(
        seriesInstanceUid: SeriesInstanceUid
    ): RenderableVolume | undefined {
        const entry = this.registry.entryFor(seriesInstanceUid);
        return entry && this.holds(entry) ? this.volumeOf(entry) : undefined;
    }

    /**
     * Registry notifications cover every register and unregister; a
     * committed change to an image the adapter has described is reported
     * once its metadata is read again.
     */
    public subscribe(listener: () => void): () => void {
        const stopLifecycle = this.registry.subscribeLifecycle(listener);
        const stopChanges = this.domainChanges?.onChangeset((changes) => {
            const imageIds = new Set(
                changes.flatMap((change) =>
                    change.kind === 'image' ? [change.id] : []
                )
            );
            if (imageIds.size > 0 && this.refresh(imageIds)) {
                listener();
            }
        });
        return () => {
            stopLifecycle();
            stopChanges?.();
        };
    }

    /**
     * Reads again the metadata of the registered images whose ids are in
     * `imageIds`. Returns whether any of them was described before.
     */
    private refresh(imageIds: ReadonlySet<string>): boolean {
        let refreshed = false;
        for (const entry of this.registry.listEntries()) {
            const cached = this.volumeCache.get(entry.image);
            if (cached && imageIds.has(entry.image.id)) {
                cached.metadata = toDatasetMetadata(entry);
                refreshed = true;
            }
        }
        if (refreshed) {
            this.listed = undefined;
        }
        return refreshed;
    }

    private holds(entry: DatasetRegistryEntry): boolean {
        return this.patient?.images.includes(entry.image) ?? false;
    }

    private volumeOf(entry: DatasetRegistryEntry): RenderableVolume {
        const cached = this.volumeCache.get(entry.image);
        if (cached) {
            return cached.volume;
        }
        const created: CachedVolume = {
            metadata: toDatasetMetadata(entry),
            volume: {
                get metadata() {
                    return created.metadata;
                },
            },
        };
        this.volumeCache.set(entry.image, created);
        return created.volume;
    }
}

/**
 * Whether two entry lists describe the same datasets: the same series, in
 * the same order, each with the same image. A fusion, set or dose change
 * replaces an entry but not its dataset.
 */
function sameDatasets(
    before: ReadonlyArray<DatasetRegistryEntry>,
    after: ReadonlyArray<DatasetRegistryEntry>
): boolean {
    return (
        before.length === after.length &&
        before.every(
            (entry, index) =>
                entry.seriesUid === after[index].seriesUid &&
                entry.image === after[index].image
        )
    );
}

function toDatasetMetadata(entry: DatasetRegistryEntry): DatasetMetadata {
    const image = entry.image;
    return {
        ...seriesSummaryOf(image, localization.t('unknown.series')),
        seriesInstanceUid: entry.seriesUid,
        frameOfReferenceUid: toFrameOfReferenceUid(image.frameOfReferenceUid),
    };
}
